/* __next_internal_client_entry_do_not_use__ AppInitializer auto */ import { jsx as _jsx } from "react/jsx-runtime";
import { PageLoadEvent } from "@faire/retailer-visitor-shared/services/performance/const";
import { PerformanceMarker } from "@faire/retailer-visitor-shared/services/performance/PerformanceMarker";
import { ElevatePortalStore } from "@faire/retailer-visitor-shared/stores/domain/ElevatePortalStore";
import { UserStore } from "@faire/retailer-visitor-shared/stores/domain/UserStore";
import { getGlobalProperty } from "@faire/web--source/common/globals/getGlobalProperty";
import { getWindow } from "@faire/web--source/common/globals/getWindow";
import { HistoryTiming } from "@faire/web--source/common/performance/HistoryTiming";
import { ResourceTimingCollector } from "@faire/web--source/common/performance/ResourceTimingCollector";
import { reloadExpiredSessions } from "@faire/web--source/common/releaseReloader";
import { initializeScrollbarWidth } from "@faire/web--source/common/scrollbarSize";
import { setImageDPR } from "@faire/web--source/common/transform-images";
import { trackRetailersReactAppInitView } from "@faire/web-api--source/events/retailers/view/reactAppInit";
import { trackSessionsLocalusertimeView } from "@faire/web-api--source/events/sessions/view/localusertime";
import once from "lodash/once";
import React, { lazy, Suspense, useLayoutEffect } from "react";
import { initializeAutoSignUp } from "@faire/retailer-visitor-shared/initializers/autoSignUp";
import { fetchBrandRetailer } from "@faire/retailer-visitor-shared/initializers/fetchBrandRetailer";
import { initializeResetPasswordModal } from "@faire/retailer-visitor-shared/initializers/initializeResetPasswordAfterSignIn";
import { startLocationLogging } from "@faire/retailer-visitor-shared/initializers/locationLogging";
import { watchForRedirects } from "@faire/retailer-visitor-shared/initializers/watchForRedirects";
import { updateEmployeeState } from "@faire/retailer-visitor-shared/lib/employee";
import { isChromeLighthouse } from "@faire/retailer-visitor-shared/lib/user-agent/isChromeLighthouse";
var initializeApp = once(function(history) {
    var _getWindow;
    var recordLifecycleEvents = getGlobalProperty("recordLifecycleEvents", false);
    if (recordLifecycleEvents) {
        var htmlSource = "nextjs";
        trackRetailersReactAppInitView(htmlSource, getGlobalProperty("routeProductArea", ""), getGlobalProperty("templatedPath", ""));
    }
    var performanceMarker = PerformanceMarker.get();
    performanceMarker.init();
    performanceMarker.mark(PageLoadEvent.appStart);
    var dpr = isChromeLighthouse() ? 1 : (_getWindow = getWindow()) === null || _getWindow === void 0 ? void 0 : _getWindow.devicePixelRatio;
    setImageDPR(dpr);
    fetchBrandRetailer();
    startLocationLogging(history);
    watchForRedirects(history);
    reloadExpiredSessions();
    initializeScrollbarWidth();
    // We used to await this in the legacy app, but blocking doesn't seem to be necessary.
    ElevatePortalStore.get().waitToInitialize();
    updateEmployeeState(UserStore.get().user);
    initializeResetPasswordModal(history);
    initializeAutoSignUp();
    trackSessionsLocalusertimeView("".concat(new Date().valueOf()));
    ResourceTimingCollector.get().initialize();
    HistoryTiming.get().initialize(history);
    performanceMarker.mark(PageLoadEvent.appInitialized);
});
/**
 * Executes the necessary steps of src/main.tsx and src/initializeApp.ts
 * on the client of the nextjs app.
 */ var useAppInitialization = function(memoryHistory) {
    useLayoutEffect(function() {
        initializeApp(memoryHistory);
    }, [
        memoryHistory
    ]);
};
var LazyPostInteractiveInitializer = /*#__PURE__*/ lazy(function() {
    return import(/* webpackChunkName: "post-app-initializer" */ "@faire/retailer-visitor-shared/app/_lib/initialization/PostInteractiveInitializer").then(function(m) {
        return {
            default: m.PostInteractiveInitializer
        };
    });
});
export var AppInitializer = function(param) {
    var history = param.history;
    useAppInitialization(history);
    return /*#__PURE__*/ _jsx(Suspense, {
        fallback: null,
        children: /*#__PURE__*/ _jsx(LazyPostInteractiveInitializer, {
            history: history
        })
    });
};
